import styles from './Help.module.css';

import { DateTime } from 'luxon';

import { useState, useEffect, useCallback } from 'react';
import { Link } from "react-router-dom";

import { onAuthStateChanged, sendPasswordResetEmail, sendEmailVerification, EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { useAuth } from '../../AuthContext';
import { useInterval } from '../../hooks/useInterval';

export const Help = () => {
    const [ message, setMessage ] = useState(null);
    const [ error, setError ] = useState(null);
    const [ user, setUser ] = useState(null);
    const [ email, setEmail ] = useState(null);
    const [ resend, setResend ] = useState(0);
    const [ verifySent, setVerifySent ] = useState(null);
    const [ resetSent, setResetSent ] = useState(null);
    const [ currentPassword, setCurrentPassword ] = useState('');
    const [ newPassword, setNewPassword ] = useState('');
    const [ confirmPassword, setConfirmPassword ] = useState('');
    const [ passwordMismatch, setPasswordMismatch ] = useState(false);
    const [ passwordValidationError, setPasswordValidationError ] = useState('');
    const [ showCurrentPassword, setShowCurrentPassword ] = useState(false);
    const [ showNewPassword, setShowNewPassword ] = useState(false);
    const [ showConfirmPassword, setShowConfirmPassword ] = useState(false);

    const { auth } = useAuth();

    const validatePassword = useCallback((password) => {
        if (password.length < 10) return 'Password must be at least 10 characters long.';
        if (password === currentPassword) return 'New password must be different from current password.';
        if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter.';
        if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter.';
        if (!/\d/.test(password)) return 'Password must contain at least one number.';

        return null; // valid
    }, [ currentPassword ]);

    const handleResendEmail = async () => {
        await sendEmailVerification(auth.currentUser, { url: process.env.REACT_APP_EMAIL_RETURN_URL })
            .then(() => {
                setVerifySent(DateTime.now().toMillis());
                setResetSent(null);
            })
            .catch(async (error) => {
                setError(error.message);
                setVerifySent(null);
            });
    };

    const handleResetPassword = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!email || !emailRegex.test(email)) {
            setError('Please enter a valid email address.');

            return;
        }

        await sendPasswordResetEmail(auth, email, { url: process.env.REACT_APP_EMAIL_RETURN_URL })
            .then(() => {
                setResetSent(DateTime.now().toMillis());
                setVerifySent(null);
                setMessage('Password reset email sent. Please check your inbox.');
                setError(null);
            })
            .catch(async (error) => {
                setError(error.message);
                setResetSent(null);
            });
    };

    const handleChangePassword = async () => {
        setError(null);
        setMessage(null);

        if (newPassword !== confirmPassword) {
            setError('New passwords do not match.');

            return;
        }

        const validationError = validatePassword(newPassword);

        if (validationError) {
            setError(validationError);

            return;
        }

        try {
            const credential = EmailAuthProvider.credential(user.email, currentPassword);

            await reauthenticateWithCredential(auth.currentUser, credential);
            await updatePassword(auth.currentUser, newPassword);

            setMessage('Password changed successfully.');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');

            // Clear the success message after 5 seconds
            setTimeout(() => setMessage(null), 5000);
        } catch (error) {
            setError(error.message);
        }
    };

    useInterval(() => {
        const now = DateTime.now().toMillis();
        const emailSent = verifySent || resetSent;

        if (emailSent) {
            const elapsed = 30 - Math.floor((now - emailSent) / 1000);

            if (elapsed <= 0) {
                setVerifySent(null);
                setResetSent(null);
                setMessage(null); // Clear email-related message after 30 seconds
            }

            setResend(Math.max(0, elapsed));
        } else {
            setVerifySent(null);
            setResetSent(null);
            setResend(0);
            // Removed setMessage(null) to avoid clearing password change messages
        }
    }, 500);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUser(user);
            setEmail(user ? user.email : null);
        });

        return unsubscribe;
    }, [ auth ]);

    useEffect(() => {
        if (newPassword && confirmPassword) {
            setPasswordMismatch(newPassword !== confirmPassword);
        } else {
            setPasswordMismatch(false);
        }
    }, [ newPassword, confirmPassword ]);

    useEffect(() => {
        if (newPassword) {
            const error = validatePassword(newPassword);

            setPasswordValidationError(error || '');
        } else {
            setPasswordValidationError('');
        }
    }, [ newPassword, validatePassword ]);

    return (
        <div className={ styles.help }>
            <div className={ styles.content }>
                <FormControl variant='outlined'>
                    <OutlinedInput
                        id="email"
                        type="email"
                        placeholder="Email"
                        autoComplete="email"
                        required
                        value={ email || '' }
                        disabled={ user ? true : false }
                        onChange={ (e) => setEmail(e.target.value) }
                        variant="outlined"
                        fullWidth
                        sx={ {
                            mb: 1,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: !user?.emailVerified ? 'orange' : 'green',
                                },
                            },
                        } }
                    />
                </FormControl>

                <Divider variant="middle" flexItem sx={ { my: 2 } } />

                { user && !user.emailVerified && (
                    <Stack align="center" spacing={ 2 } sx={ { p: 2, mb: 2 } }>
                        <Alert variant='filled' severity="warning" sx={ { mt: 2, mb: 1 } }>
                            <AlertTitle>
                                { verifySent ? 'Please check your inbox or spam folder and click the verification link.' : 'Your email address is not yet verified.' }
                            </AlertTitle>
                        </Alert>

                        <Button
                            id="resendEmailButton"
                            onClick={ handleResendEmail }
                            disabled={ resend }
                            variant='outlined'
                        >
                            { verifySent ? 'try again in ' + resend + 's' : 'resend email verification' }
                        </Button>
                    </Stack>
                ) }

                { user && <Divider variant="middle" flexItem sx={ { mb: 4 } } /> }

                { user && (
                    <div className={ styles.passwordChange }>
                        <Stack spacing={ 1 } sx={ { width: '100%', maxWidth: '400px' } }>
                            <TextField
                                label="Current Password"
                                type={ showCurrentPassword ? 'text' : 'password' }
                                value={ currentPassword }
                                onChange={ (e) => setCurrentPassword(e.target.value) }
                                variant="outlined"
                                sx={ { mb: 1 } }
                                slotProps={ {
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={ () => setShowCurrentPassword(!showCurrentPassword) }
                                                    edge="end"
                                                >
                                                    { showCurrentPassword ? <VisibilityOff /> : <Visibility /> }
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }
                                } }
                            />

                            <FormControl variant="outlined" sx={ { width: '100%' } }>
                                <InputLabel htmlFor="new-password">New Password</InputLabel>

                                <OutlinedInput
                                    id="new-password"
                                    label="New Password"
                                    type={ showNewPassword ? 'text' : 'password' }
                                    value={ newPassword }
                                    onChange={ (e) => setNewPassword(e.target.value) }
                                    variant="outlined"
                                    sx={ { mb: 1 } }
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={ () => setShowNewPassword(!showNewPassword) }
                                                edge="end"
                                            >
                                                { showNewPassword ? <VisibilityOff /> : <Visibility /> }
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>

                            <FormControl variant="outlined" sx={ { width: '100%' } }>
                                <InputLabel htmlFor="confirm-password">Confirm New Password</InputLabel>

                                <OutlinedInput
                                    id="confirm-password"
                                    label="Confirm New Password"
                                    type={ showConfirmPassword ? 'text' : 'password' }
                                    value={ confirmPassword }
                                    onChange={ (e) => setConfirmPassword(e.target.value) }
                                    variant="outlined"
                                    sx={ { mb: 2 } }
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={ () => setShowConfirmPassword(!showConfirmPassword) }
                                                edge="end"
                                            >
                                                { showConfirmPassword ? <VisibilityOff /> : <Visibility /> }
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>

                            { passwordValidationError && (
                                <Alert severity="warning" sx={ { width: '100%', mb: 1 } }>
                                    { passwordValidationError }
                                </Alert>
                            ) }

                            { passwordMismatch && (
                                <Alert variant='filled' severity="error" sx={ { width: '100%' } }>
                                    Passwords do not match.
                                </Alert>
                            ) }

                            <Button
                                onClick={ handleChangePassword }
                                variant="contained"
                                fullWidth
                                disabled={ !currentPassword || !newPassword || !confirmPassword || passwordMismatch }
                                sx={ { textTransform: 'none' } }
                            >
                                Change Password
                            </Button>
                        </Stack>
                    </div>
                ) }

                { !user && (
                    <div className={ styles.forgotPassword }>
                        <Typography variant="body2" sx={ { mt: 2, mb: 1 } }>
                            { `Forgot your password? We'll send a reset link to your email.` }
                        </Typography>

                        <Button
                            id="resetPasswordButton"
                            variant="text"
                            color="primary"
                            onClick={ resend ? null : handleResetPassword }
                            disabled={ resend }
                            sx={ { textTransform: 'none' } }
                        >
                            { resetSent ? 'Email sent. try again in ' + resend + 's' : 'reset password' }
                        </Button>
                    </div>
                ) }
            </div>

            {
                error && (
                    <Alert variant='filled' severity="error" sx={ { mt: 2, mb: 1 } }>
                        { error }
                    </Alert>
                )
            }

            {
                message && (
                    <Alert variant='filled' severity="success" sx={ { mt: 1, mb: 1 } }>
                        { message }
                    </Alert>
                )
            }

            {
                (!user || user?.emailVerified) && <div className={ styles.close }>
                    <Link to="/">return to home page</Link>
                </div>
            }
        </div >
    )
};